<template>
  <div class="digital-curator-theme antialiased min-h-screen">
    <!-- Main Wrapper -->
    <div class="pt-8 pb-12 px-2 lg:px-8 max-w-[1600px] mx-auto">

      <div class="vx-row">

        <!-- Left Sidebar: Signers & Methods -->
        <aside class="vx-col w-full lg:w-1/3 mb-10 sidebar-sticky">

          <!-- Header dentro del sidebar (Una sola línea) -->
          <div class="flex items-center justify-between gap-4 mb-4 pb-3 border-b border-surface-container-high">
            <div class="flex items-center gap-4 min-w-0 overflow-hidden">
              <div class="sidebar-header-icon shrink-0">
                <feather-icon icon="FileTextIcon" svgClasses="w-5 h-5 text-primary" />
              </div>
              <div class="flex items-center gap-3 min-w-0 overflow-hidden">
                <h1 class="text-xl font-bold font-headline text-on-surface mb-0 whitespace-nowrap">Preparar Documento</h1>
                <div class="h-4 w-px bg-surface-container-highest shrink-0"></div>
                <p class="text-xs text-on-surface-variant font-label m-0 truncate opacity-70">Configura firmantes, firmas y opciones</p>
              </div>
            </div>
            <a href="https://youtu.be/AkL1N9PHgTc?si=fQ9VzMFK8LC-KM7I" target="_blank" rel="noopener noreferrer" class="text-primary font-semibold text-sm italic underline cursor-pointer shrink-0 ml-auto">
              ¿Cómo funciona?
            </a>
          </div>

          <section class="flex flex-col gap-5">

            <!-- ── Firmantes ─────────────────────────────── -->
            <div>
              <div class="section-label-row">
                <div class="section-label-icon" style="background: rgba(var(--vs-primary), 0.1);">
                  <feather-icon icon="UsersIcon" svgClasses="w-3.5 h-3.5" style="color: rgba(var(--vs-primary), 1);" />
                </div>
                <h3 class="section-label">Firmantes</h3>
                <vx-tooltip text="Personas que deberán firmar el documento. Puedes añadir varios y configurar restricciones individuales." position="right">
                  <feather-icon icon="InfoIcon" svgClasses="w-3.5 h-3.5 text-on-surface-variant opacity-50 cursor-pointer hover:opacity-100 transition-opacity" />
                </vx-tooltip>
              </div>

              <div class="space-y-4">
                <!-- Toggle firma ordenada -->
                <div class="option-row">
                  <div class="option-row-left">
                    <div class="option-icon" style="background:rgba(100,100,100,0.08);">
                      <feather-icon icon="ListIcon" svgClasses="w-3.5 h-3.5 text-on-surface-variant" />
                    </div>
                    <div>
                      <p class="option-title">Firma ordenada</p>
                      <p class="option-desc">Cada firmante recibe la solicitud solo cuando el anterior ha completado su firma.</p>
                    </div>
                  </div>
                  <vs-switch v-model="sortableSignatures" class="ml-3 shrink-0"/>
                </div>

                <p class="mt-2 text-xs text-primary font-medium" v-if="sortableSignatures">
                  * Firma ordenada habilitada. Arrastra las tarjetas para establecer el orden de firma.
                </p>

                <draggable v-model="signatures" :disabled="!sortableSignatures" handle=".drag-handle" ghost-class="dragging" class="w-full">
                  <transition-group name="list" tag="div" class="w-full">
                    <SignerCard
                      v-for="({ signer, originalIndex }, displayIndex) in uniqueSignaturesForDisplay"
                      :key="signer.id"
                      :signer="signer"
                      :index="originalIndex"
                      :display-index="displayIndex"
                      :all-signers="signatures"
                      :sortable="sortableSignatures"
                      :sign-position="signPosition"
                      :sign-mode="signMode"
                      :has-pdf="!!pdf"
                      @remove-signer="removeSigner"
                      @popUpLimitFunction="popUpLimitFunction"
                      @search-signers="searchSigners"
                      :email-suggestions="emailSuggestions"
                      :ref="'signerCard' + originalIndex"
                      class="mb-4 shadow-sm"
                      @place-signer="onPlaceSigner"
                      @clear-signer="onClearSigner"
                    />
                  </transition-group>
                </draggable>

                <div class="flex justify-end w-full mt-2">
                  <vs-button type="border" color="primary" icon-pack="feather" icon="icon-plus" @click="addNewSigner">Añadir firmante</vs-button>
                </div>
              </div>
            </div>

            <!-- ── Espectadores ───────────────────────────── -->
            <div class="transition-all">
              <div class="section-label-row">
                <div class="section-label-icon" style="background: rgba(var(--vs-primary), 0.1);">
                  <feather-icon icon="EyeIcon" svgClasses="w-3.5 h-3.5" style="color: rgba(var(--vs-primary), 1);" />
                </div>
                <h3 class="section-label">Espectadores</h3>
                <vx-tooltip text="Los espectadores serán notificados sobre el estado del documento y recibirán una copia del mismo una vez firmado pero no podrán firmar el documento.
" position="right">
                  <feather-icon icon="InfoIcon" svgClasses="w-3.5 h-3.5 text-on-surface-variant opacity-50 cursor-pointer hover:opacity-100 transition-opacity" />
                </vx-tooltip>
                <vs-button v-if="spectators.length > 0" type="flat" size="small" class="ml-auto p-0" @click="spectatorPopupEnable = true">Gestionar</vs-button>
              </div>

              <div v-if="spectators.length > 0" class="bg-surface-container-low rounded-2xl p-4 space-y-3 shadow-sm border border-surface-container-high/50 mb-4">
                <div v-for="listItem in spectators" :key="listItem.email" class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-9 h-9 rounded-full bg-surface-container-highest text-on-surface font-bold flex items-center justify-center text-xs border border-surface-container-high/30">
                      {{ listItem.name.split(' ').slice(0,2).map(n => n.charAt(0)).join('').toUpperCase() || 'E' }}
                    </div>
                    <div>
                      <p class="text-sm font-bold text-on-surface leading-tight mb-0">{{ listItem.name }}</p>
                      <p class="text-xs text-on-surface-variant leading-tight">{{ listItem.email }}</p>
                    </div>
                  </div>
                  <feather-icon icon="EyeIcon" svgClasses="w-4 h-4 text-on-surface-variant/60 shrink-0" />
                </div>
              </div>

              <div v-else class="empty-state-row mb-4">
                <feather-icon icon="EyeOffIcon" svgClasses="w-4 h-4 text-on-surface-variant opacity-60 mr-2 shrink-0" />
                <p class="text-[13px] text-on-surface-variant m-0">Aún no hay espectadores asignados</p>
              </div>

              <div class="flex justify-end w-full">
                <vs-button type="border" color="primary" icon-pack="feather" icon="icon-plus" @click="spectatorPopupEnable = true">Añadir espectador</vs-button>
              </div>
            </div>

            <!-- ── Configuraciones ───────────────────────── -->
            <div>
              <div class="section-label-row">
                <div class="section-label-icon" style="background: rgba(var(--vs-primary), 0.1);">
                  <feather-icon icon="SlidersIcon" svgClasses="w-3.5 h-3.5" style="color: rgba(var(--vs-primary), 1);" />
                </div>
                <h3 class="section-label">Configuraciones</h3>
              </div>

              <div class="p-5 bg-surface-container-low rounded-xl">

                <!-- Tipos de firma -->
                <div class="config-sub-header">
                  <feather-icon icon="CheckSquareIcon" svgClasses="w-3.5 h-3.5 text-on-surface-variant mr-3" />
                  <span>Métodos de firma admitidos</span>
                  <vx-tooltip text="Define qué métodos de firmado podrán usar los firmantes para completar el proceso." position="right">
                    <feather-icon icon="InfoIcon" svgClasses="w-3 h-3 text-on-surface-variant opacity-40 cursor-pointer hover:opacity-90 ml-3 transition-opacity" />
                  </vx-tooltip>
                </div>

                <div class="signature-types-grid mt-3 mb-6">
                  <!-- Firma con identidad -->
                  <div class="signature-type-card" :class="{ 'active': signatureType.includes('VERIFICAMEX'), 'disabled': !canDisableSimpleAndFirelType }">
                    <div class="card-content" @click="toggleSignatureType('VERIFICAMEX')">
                      <div class="icon-wrapper">
                        <img src="@/assets/images/document/id.svg" alt="ID" class="signature-icon">
                      </div>
                      <h4>Con identidad</h4>
                      <p class="description">(requiere INE)</p>
                      <div @click.stop>
                        <vs-checkbox v-model="signatureType" vs-value="VERIFICAMEX" :disabled="!canDisableSimpleAndFirelType" @change="toggleSignatureType('VERIFICAMEX', true)" />
                      </div>
                    </div>
                  </div>

                  <!-- Firma simple -->
                  <div class="signature-type-card" :class="{ 'active': signatureType.includes('VERIFICAMEX_LOCAL'), 'disabled': !canDisableSimpleAndFirelType }">
                    <div class="card-content" @click="toggleSignatureType('VERIFICAMEX_LOCAL')">
                      <div class="icon-wrapper">
                        <img src="@/assets/images/document/sign.svg" alt="Sign" class="signature-icon">
                      </div>
                      <h4>Firma simple</h4>
                      <p class="description">(extranjeros)</p>
                      <div @click.stop>
                        <vs-checkbox v-model="signatureType" vs-value="VERIFICAMEX_LOCAL" :disabled="!canDisableSimpleAndFirelType" @change="toggleSignatureType('VERIFICAMEX_LOCAL', true)" />
                      </div>
                    </div>
                  </div>

                  <!-- e.firma SAT -->
                  <div class="signature-type-card" :class="{ 'active': signatureType.includes('EFIRMA'), 'disabled': !canDisableEfirmaType }">
                    <div class="card-content" @click="toggleSignatureType('EFIRMA')">
                      <div class="icon-wrapper">
                        <img src="@/assets/images/document/sat.svg" alt="SAT" class="signature-icon">
                      </div>
                      <h4>e.firma</h4>
                      <p class="description">(SAT)</p>
                      <div @click.stop>
                        <vs-checkbox v-model="signatureType" vs-value="EFIRMA" :disabled="!canDisableEfirmaType" @change="toggleSignatureType('EFIRMA', true)" />
                      </div>
                    </div>
                  </div>

                  <!-- FIREL -->
                  <div class="signature-type-card" :class="{ 'active': signatureType.includes('FIREL'), 'disabled': !canDisableSimpleAndFirelType }">
                    <div class="card-content" @click="toggleSignatureType('FIREL')">
                      <div class="icon-wrapper">
                        <img src="@/assets/images/document/firel.svg" alt="FIREL" class="signature-icon">
                      </div>
                      <h4>FIREL</h4>
                      <p class="description">(PJF)</p>
                      <div @click.stop>
                        <vs-checkbox v-model="signatureType" vs-value="FIREL" :disabled="!canDisableSimpleAndFirelType" @change="toggleSignatureType('FIREL', true)" />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Opciones avanzadas -->
                <div class="border-t border-surface-container-high pt-4">
                  <div class="config-sub-header mb-3">
                    <feather-icon icon="SettingsIcon" svgClasses="w-3.5 h-3.5 text-on-surface-variant mr-3" />
                    <span>Opciones avanzadas</span>
                    <vx-tooltip text="Configura recordatorios automáticos, logotipo en el documento y cómo se representará la firma." position="right">
                      <feather-icon icon="InfoIcon" svgClasses="w-3 h-3 text-on-surface-variant opacity-40 cursor-pointer hover:opacity-90 ml-3 transition-opacity" />
                    </vx-tooltip>
                  </div>
                  <div class="flex flex-col gap-2">

                    <!-- Recordatorios -->
                    <div @click="rememberPopupEnable = true" class="advanced-option-row group">
                      <div class="advanced-option-icon group-hover:bg-primary transition-colors">
                        <feather-icon icon="ClockIcon" svgClasses="w-4 h-4 text-primary group-hover:text-white transition-colors" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="advanced-option-title">Recordatorios</p>
                        <p class="advanced-option-desc">Envía notificaciones automáticas a quienes aún no han firmado.</p>
                      </div>
                      <feather-icon icon="ChevronRightIcon" svgClasses="w-4 h-4 text-on-surface-variant opacity-40 shrink-0 group-hover:opacity-80 transition-opacity" />
                    </div>

                    <!-- Representación de firmado -->
                    <div @click="signedMethodPopupEnable = true" class="advanced-option-row group">
                      <div class="advanced-option-icon group-hover:bg-primary transition-colors">
                        <feather-icon icon="FileTextIcon" svgClasses="w-4 h-4 text-primary group-hover:text-white transition-colors" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="advanced-option-title">Representación de firmado</p>
                        <p class="advanced-option-desc">Elige cómo se visualizará la firma en el documento (posición libre o fija).</p>
                      </div>
                      <feather-icon icon="ChevronRightIcon" svgClasses="w-4 h-4 text-on-surface-variant opacity-40 shrink-0 group-hover:opacity-80 transition-opacity" />
                    </div>

                    <!-- Logo de hoja -->
                    <div v-if="signPosition === 'NONE'" @click="customLogo = true" class="advanced-option-row group">
                      <div class="advanced-option-icon group-hover:bg-primary transition-colors">
                        <feather-icon icon="ImageIcon" svgClasses="w-4 h-4 text-primary group-hover:text-white transition-colors" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="advanced-option-title">Personaliza tu logo</p>
                        <p class="advanced-option-desc">Personaliza el encabezado del documento con el logotipo de tu empresa.</p>
                      </div>
                      <feather-icon icon="ChevronRightIcon" svgClasses="w-4 h-4 text-on-surface-variant opacity-40 shrink-0 group-hover:opacity-80 transition-opacity" />
                    </div>

                    <!-- Hoja de firmas (solo en CLASSIC/NONE) -->
                    <div v-if="signPosition === 'NONE'" class="advanced-option-row group">
                      <div class="advanced-option-icon group-hover:bg-primary transition-colors">
                        <feather-icon icon="FilePlusIcon" svgClasses="w-4 h-4 text-primary group-hover:text-white transition-colors" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center mb-0.5">
                          <p class="advanced-option-title m-0">Hoja de firmas</p>
                          <vx-tooltip text="La hoja de firmas es obligatoria en el modo de Firma Electrónica Avanzada." position="top">
                            <feather-icon icon="InfoIcon" svgClasses="w-3 h-3 text-on-surface-variant opacity-40 cursor-pointer hover:opacity-90 ml-2 transition-opacity" />
                          </vx-tooltip>
                        </div>
                        <p class="advanced-option-desc m-0">Se agregará automáticamente una página extra al final.</p>
                      </div>
                      <vs-switch :value="true" disabled class="shrink-0" />
                    </div>

                    <!-- Mi branding: logo de la carpeta en correos y pantalla de firma, independiente de la hoja de firmas -->
                    <div class="advanced-option-row group">
                      <div class="advanced-option-icon group-hover:bg-primary transition-colors">
                        <feather-icon icon="AwardIcon" svgClasses="w-4 h-4 text-primary group-hover:text-white transition-colors" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="advanced-option-title">Habilitar mi branding</p>
                        <p class="advanced-option-desc">{{ customBrandingDescription }}</p>
                      </div>
                      <vs-switch v-if="companyHasLogo && logoMode === 'MEDIUM_BRANDING'" :value="true" disabled class="shrink-0" />
                      <vs-switch v-else v-model="customBranding" :disabled="!companyHasLogo" class="shrink-0" />
                    </div>

                  </div>
                </div>

              </div>
            </div>
          </section>
        </aside>

        <!-- Main Content: Document Viewer -->
        <section class="vx-col w-full lg:w-2/3 flex flex-col">
          <div class="bg-surface-container-high p-4 rounded-xl flex justify-center relative overflow-hidden" :style="pdf ? 'min-height: 700px;' : 'min-height: 500px;'">
            
            <div v-if="!pdf" class="w-full h-full flex items-center justify-center p-8">
              <div class="upload-drop-zone-wrapper">
                <input type="file" ref="fileInput" class="hidden" accept=".pdf" @change="handleClick">
                <div
                  class="upload-drop-zone"
                  @click="$refs.fileInput.click()"
                  @drop="handleDrop"
                  @dragover="handleDragover"
                  @dragenter="handleDragover"
                >
                  <!-- Ícono -->
                  <div class="upload-icon-box">
                    <feather-icon icon="UploadCloudIcon" svgClasses="w-8 h-8 text-white" />
                  </div>

                  <!-- Textos -->
                  <h3 class="upload-title">Sube tu documento</h3>
                  <p class="upload-subtitle">Arrastra y suelta tu archivo PDF aquí para<br>iniciar el proceso de firmado.</p>

                  <!-- Botón -->
                  <vs-button color="primary" type="filled" icon-pack="feather" icon="icon-folder" @click.stop="$refs.fileInput.click()">Seleccionar archivo</vs-button>

                  <p class="upload-hint">Solo archivos PDF · Máx. 49 MB</p>
                </div>
              </div>
            </div>

            <div v-else class="w-full h-full flex flex-col">
              <!-- Info del modo CUSTOM integrado -->
              <div v-if="signMode !== 'CLASSIC'" class="px-6 py-4 bg-surface-container-lowest border border-surface-container-high rounded-t-xl z-20 flex items-center justify-between w-full relative editorial-shadow mb-[-4px]">
                 <div class="flex items-center">
                   <div class="w-8 h-8 rounded-full bg-primary-fixed flex items-center justify-center mr-3">
                     <feather-icon icon="InfoIcon" svgClasses="w-4 h-4 text-primary" />
                   </div>
                   <span class="text-sm font-medium text-on-surface">Arrastra un recuadro de firma para cada participante sobre el documento.</span>
                 </div>
              </div>

              <!-- Integración directa de PdfSignaturePlacer o ReactPdfViewer en base al signMode -->
              <div class="w-full h-full bg-surface-container-lowest relative p-0 overflow-auto flex justify-center rounded-b-xl editorial-shadow" style="min-height: 600px;">
                 <PdfSignaturePlacer
                  v-if="rawBase64Pdf && signMode !== 'CLASSIC'"
                  ref="pdfPlacer"
                  :pdf-base64="rawBase64Pdf"
                  :signers="signatures"
                  class="w-full h-full"
                  @duplicate-signer="duplicateSigner"
                  @remove-signer-instance="removeSignerInstance"
                  @repeat-signer-on-pages="repeatSignerOnPages"
                 />
                 <ReactPdfViewer
                  v-else-if="base64Pdf && signMode === 'CLASSIC'"
                  class="background-fb card-p w-full h-full"
                  :pdf="base64Pdf"
                  height="600px"
                 />
                 <div v-else class="flex w-full h-full justify-center items-center">
                   <p class="text-on-surface-variant font-label text-sm animate-pulse">Procesando documento...</p>
                 </div>
              </div>
            </div>

          </div>

          <!-- Footer Action -->
          <div class="mt-8 flex justify-between items-center bg-transparent mt-5 px-2">
            <vs-button v-if="pdf" color="primary" type="border" icon-pack="feather" icon="icon-refresh-ccw" @click="resetDocument">Cambiar Documento</vs-button>
            <div v-else></div>
            
            <div class="flex gap-4">
              <vs-button 
                color="primary" 
                type="filled" 
                icon-pack="feather" 
                :icon="pdf ? 'icon-send' : 'icon-upload'" 
                :icon-after="!!pdf" 
                @click="pdf ? formSubmitted() : $refs.fileInput.click()" 
                :disabled="!pdf && uniqueSignaturesForDisplay.length === 0"
                class="vs-con-loading__container"
              >
                {{ pdf ? 'Confirmar y Enviar' : 'Sube un PDF para continuar' }}
              </vs-button>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Poupups Originales -->
    <vs-popup class="signed_method" title="Selecciona un Método de Firmado" :active.sync="signedMethodPopupEnable">
      <sign-selector :set-sign-position="setSignPosition" :submit-new-document="signSelectorAction" :current-mode="signPosition === 'NONE' ? 'CLASSIC' : 'PDF_SIGNATURE'"></sign-selector>
    </vs-popup>

    <vs-popup class="modern-branding-popup" title="Recordatorios Automáticos" :active.sync="rememberPopupEnable">
      <div class="px-5 pb-5 pt-3">
        <!-- Encabezado Compacto sin blancos excesivos -->
        <div class="flex items-center gap-3 mb-6">
           <div class="w-10 h-10 bg-primary-fixed rounded-lg flex items-center justify-center shrink-0">
              <feather-icon icon="BellIcon" svgClasses="w-5 h-5 text-primary" />
           </div>
           <div>
              <h3 class="text-base font-bold font-headline text-on-surface">Plan de Seguimiento</h3>
              <p class="text-xs font-label text-on-surface-variant m-0">Define los recordatorios para los firmantes que aún no han firmado el documento.</p>
           </div>
        </div>

        <div class="bg-surface-container-lowest border-2 border-surface-container-high rounded-xl p-5 mb-6">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            <div class="flex flex-col items-start w-full">
               <label class="text-xs font-bold text-on-surface block mb-2">Máximo de correos</label>
               <vs-input-number min="1" max="7" v-model="tries" class="m-0 w-full" />
            </div>
            
            <div class="flex flex-col items-start w-full">
               <label class="text-xs font-bold text-on-surface block mb-2">Días de separación</label>
               <vs-input-number min="1" max="7" v-model="remember_every" class="m-0 w-full" />
            </div>

            <div class="flex flex-col items-start w-full">
               <label class="text-xs font-bold text-on-surface block mb-2">Hora de transmisión</label>
               <div class="flex items-center gap-2 border-2 border-surface-container-high bg-white rounded-lg px-3 py-1.5 shadow-sm focus-within:border-primary transition-colors cursor-pointer w-full" style="height: 38px;">
                 <feather-icon icon="ClockIcon" svgClasses="w-4 h-4 text-on-surface-variant shrink-0" />
                 <flat-pickr :config="configDateTimePicker" v-model="remember_at" placeholder="Selecciona una hora" class="border-none outline-none w-full text-on-surface font-bold text-sm bg-transparent cursor-pointer p-0 m-0 shadow-none"/>
               </div>
            </div>
          </div>
        </div>

        <!-- Línea de tiempo visual (Paso a Paso) -->
        <div class="mb-8">
           <label class="text-xs font-bold text-on-surface block mb-4 uppercase tracking-wider">Línea de envío proyectada</label>
           
           <div class="flex items-center w-full overflow-x-auto pb-8 custom-scrollbar">
             <template v-for="n in Number(tries)">
                <!-- Círculo de notificación -->
                <div :key="'step-'+n" class="flex flex-col items-center relative z-10 shrink-0">
                  <div class="w-10 h-10 rounded-full bg-primary text-white font-bold flex items-center justify-center shadow-sm border-2 border-white text-sm">
                    D{{ n === 1 ? 1 : 1 + ((n - 1) * Number(remember_every)) }}
                  </div>
                  <span class="absolute text-primary uppercase font-bold whitespace-nowrap" style="top: 42px; left: 0; right: 0; font-size: 10px; text-align: center;">{{ remember_at || '12:30' }}</span>
                </div>
                
                <!-- Línea conectora -->
                <div v-if="n < Number(tries)" :key="'line-'+n" class="flex-1 h-1 bg-primary-fixed min-w-[40px] relative z-0 -ml-1 -mr-1"></div>
             </template>
           </div>
        </div>

        <div class="mt-4 w-full flex justify-end">
           <vs-row vs-justify="flex-end">
             <vs-button type="filled" color="primary" @click="rememberPopupEnable = false">Guardar</vs-button>
           </vs-row>
        </div>
      </div>
    </vs-popup>

    <vs-popup class="modern-branding-popup" title="Personalización de Hoja" :active.sync="customLogo">
      <div class="px-5 pb-5 pt-3">
        <!-- Encabezado Compacto sin blancos excesivos -->
        <div class="flex items-center gap-3 mb-6">
           <div class="w-10 h-10 bg-primary-fixed rounded-lg flex items-center justify-center shrink-0">
              <feather-icon icon="LayoutIcon" svgClasses="w-5 h-5 text-primary" />
           </div>
           <div>
              <h3 class="text-base font-bold font-headline text-on-surface">Hoja de Evidencias</h3>
              <p class="text-xs font-label text-on-surface-variant m-0">Selecciona el diseño del encabezado para las firmas.</p>
           </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <!-- Logo Verificamex -->
          <div @click="logoMode = 'FULL_BRANDING'" 
               :class="['cursor-pointer rounded-xl border-2 transition-all p-4 flex flex-col items-center justify-center text-center relative overflow-visible group', logoMode === 'FULL_BRANDING' ? 'border-primary bg-primary-fixed shadow-sm scale-105 z-10' : 'border-surface-container-high bg-white hover:border-outline']">
            <div class="absolute top-3 left-3 z-20">
              <vs-radio v-model="logoMode" vs-name="logoModeGroup" vs-value="FULL_BRANDING"></vs-radio>
            </div>
            <img src="@/assets/images/document/full-logo.svg" class="h-28 mb-3 opacity-90 group-hover:opacity-100 transition-transform group-hover:rotate-1" alt="Verificamex">
            <h4 class="font-bold font-label text-sm text-on-surface mb-0">Logo Verificamex</h4>
            <p class="text-[11px] text-on-surface-variant font-medium mt-1">Diseño oficial</p>
          </div>

          <!-- Logo Personalizado -->
          <div @click="logoMode = 'MEDIUM_BRANDING'" 
               :class="['cursor-pointer rounded-xl border-2 transition-all p-4 flex flex-col items-center justify-center text-center relative overflow-visible group', logoMode === 'MEDIUM_BRANDING' ? 'border-primary bg-primary-fixed shadow-sm scale-105 z-10' : 'border-surface-container-high bg-white hover:border-outline']">
            <div class="absolute top-3 left-3 z-20">
              <vs-radio v-model="logoMode" vs-name="logoModeGroup" vs-value="MEDIUM_BRANDING"></vs-radio>
            </div>
            <img src="@/assets/images/document/medium-logo.svg" class="h-28 mb-3 opacity-90 group-hover:opacity-100 transition-transform group-hover:-rotate-1" alt="Tu Logo">
            <h4 class="font-bold font-label text-sm text-on-surface mb-0">Logo Personalizado</h4>
            <p class="text-[11px] text-on-surface-variant font-medium mt-1">Tu marca empresarial</p>
          </div>

          <!-- Sin Logos -->
          <div @click="logoMode = 'NO_BRANDING'" 
               :class="['cursor-pointer rounded-xl border-2 transition-all p-4 flex flex-col items-center justify-center text-center relative overflow-visible group', logoMode === 'NO_BRANDING' ? 'border-primary bg-primary-fixed shadow-sm scale-105 z-10' : 'border-surface-container-high bg-white hover:border-outline']">
            <div class="absolute top-3 left-3 z-20">
              <vs-radio v-model="logoMode" vs-name="logoModeGroup" vs-value="NO_BRANDING"></vs-radio>
            </div>
            <img src="@/assets/images/document/upper-left.svg" class="h-28 mb-3 opacity-90 group-hover:opacity-100 transition-transform group-hover:rotate-1" alt="Sin Logo">
            <h4 class="font-bold font-label text-sm text-on-surface mb-0">Neutro</h4>
            <p class="text-[11px] text-on-surface-variant font-medium mt-1">Borde limpio sin logos</p>
          </div>
        </div>

        <div class="mt-6 w-full flex justify-end">
           <vs-row vs-justify="flex-end">
             <vs-button type="filled" color="primary" @click="customLogo = false">Guardar</vs-button>
           </vs-row>
        </div>
      </div>
    </vs-popup>

    <vs-popup class="modern-branding-popup" title="Gestión de Espectadores" :active.sync="spectatorPopupEnable">
      <div class="px-5 pb-5 pt-3">
        <!-- Encabezado Compacto -->
        <div class="flex items-center gap-3 mb-6">
           <div class="w-10 h-10 bg-primary-fixed rounded-lg flex items-center justify-center shrink-0">
              <feather-icon icon="UsersIcon" svgClasses="w-5 h-5 text-primary" />
           </div>
           <div>
              <h3 class="text-base font-bold font-headline text-on-surface">Copia para Espectadores</h3>
              <p class="text-xs font-label text-on-surface-variant m-0">Añade invitados que recibirán el documento finalizado como copia.</p>
           </div>
        </div>

        <!-- Lista Actual -->
        <div class="mb-6">
           <label class="text-xs font-bold text-on-surface block mb-3 uppercase tracking-wider">Directorio Actual</label>
           
           <div v-if="spectators.length === 0" class="border-2 border-dashed border-surface-container-high rounded-xl p-8 text-center bg-surface-container-lowest">
              <feather-icon icon="UsersIcon" svgClasses="w-8 h-8 text-on-surface-variant/40 mx-auto mb-2" />
              <p class="text-sm font-medium text-on-surface-variant m-0">Lista vacía. No hay espectadores agregados.</p>
           </div>
           
           <div v-else class="space-y-3 max-h-48 overflow-y-auto custom-scrollbar pr-2">
             <div v-for="(listItem) in spectators" :key="listItem.email" class="flex items-center justify-between p-3 rounded-xl border border-surface-container-high bg-white hover:border-primary/40 transition-colors shadow-sm">
                <div class="flex items-center gap-3">
                   <div class="w-10 h-10 rounded-full bg-primary-fixed text-primary font-bold flex items-center justify-center text-sm border border-primary/20">
                     {{ listItem.name.split(' ').slice(0,2).map(n => n.charAt(0)).join('').toUpperCase() || 'E' }}
                   </div>
                   <div>
                     <p class="text-sm font-bold text-on-surface leading-tight mb-0">{{ listItem.name }}</p>
                     <p class="text-xs text-on-surface-variant leading-tight">{{ listItem.email }}</p>
                   </div>
                </div>
                <vs-button type="flat" color="danger" icon-pack="feather" icon="icon-trash-2" size="small" @click="deleteSpectator(listItem.email)" class="px-2 shrink-0 border border-danger/10 hover:bg-danger hover:text-white rounded-lg transition-all"></vs-button>
             </div>
           </div>
        </div>

        <!-- Formulario Nuevo -->
        <div class="bg-surface-container-lowest border-2 border-surface-container-high rounded-xl p-5 relative">
           <label class="text-xs font-bold text-on-surface block mb-4 uppercase tracking-wider">Añadir Nuevo Correo</label>
           
           <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
              <div>
                 <vs-input
                   :danger="!$v.spectatorsForm.email_spectator.email && $v.spectatorsForm.email_spectator.$dirty"
                   class="w-full relative-label"
                   label="Correo electrónico *"
                   placeholder="ejemplo@correo.com"
                   v-model.trim="spectatorsForm.email_spectator"
                   @blur="$v.spectatorsForm.email_spectator.$touch()"
                 ></vs-input>
                 <div class="text-danger font-medium text-[11px] mt-1 mb-2 h-4" v-if="!$v.spectatorsForm.email_spectator.email && $v.spectatorsForm.email_spectator.$dirty">
                   * Obligatorio y válido
                 </div>
                 <div v-else class="h-4 mt-1 mb-2"></div>
              </div>
              <div class="mt-px">
                 <vs-input
                   class="w-full relative-label"
                   label="Nombre completo"
                   placeholder="Juan Pérez"
                   v-model.trim="spectatorsForm.name_spectator"
                 ></vs-input>
              </div>
           </div>
           
           <div class="mt-4 w-full">
              <vs-button class="w-full" color="primary" type="filled" @click="addSpectator">
                Agregar Espectador
              </vs-button>
           </div>
        </div>
      </div>
    </vs-popup>

    <!-- Popup de confirmación para firma simple -->
    <vs-popup title="Consentimiento Firma Simple" :active.sync="lightConfirmationPopup">
      <div class="pt-3 px-3">
        <h3 class="mb-3 font-bold text-primary">Validación de Identidad</h3>
        <p class="mb-4 text-sm text-gray-700">Al seleccionar el tipo de firmado "Simple" para extranjeros, <b>es su responsabilidad verificar que los nombres y correos de las personas invitadas sean correctos</b>.</p>

        <vs-divider class="my-4"/>

        <h4 class="mb-3 text-sm font-bold">Autenticación por WhatsApp/SMS</h4>
        <vs-checkbox v-model="blockPhoneVerification" class="mb-3 font-medium">
          Bloquear código de verificación al teléfono registrado
        </vs-checkbox>
        <p class="text-xs text-gray-500 mb-4 bg-gray-50 p-3 rounded">Al activar esta opción, los firmantes deberán utilizar únicamente el número de teléfono con el que fueron registrados para recibir el código Token, aumentando drásticamente la seguridad del contrato.</p>

        <div class="text-danger bg-red-50 p-3 rounded flex items-start mt-2" v-if="!hasPhoneNumbers">
          <feather-icon icon="AlertTriangleIcon" class="mr-2 mt-1" svgClasses="h-5 w-5 text-danger" />
          <span class="text-sm text-danger font-medium">Advertencia: Algunos firmantes en su lista no tienen un número de teléfono. Le recomendamos encarecidamente añadirlos antes de enviar el documento para una mayor solidez jurídica.</span>
        </div>
      </div>
      <div>
        <vs-divider class="mt-4 mb-2"/>
        <vs-row class="mt-4" vs-justify="flex-end">
          <vs-button type="border" class="mr-3" @click="cancelLightSignatureType">Cancelar Configuración</vs-button>
          <vs-button type="filled" color="primary" @click="confirmLightSignatureType">Aceptar y Continuar</vs-button>
        </vs-row>
      </div>
    </vs-popup>

    <!-- Segundo Popup: Revisión de Firmantes (Opcional, se dispara luego del consentimiento) -->
    <vs-popup title="Revisión Final de Firmantes" :active.sync="signersReviewPopup">
      <div class="pt-3 px-3">
        <h3 class="mb-3 font-bold text-primary">Confirme los Detalles</h3>
        <p class="mb-5 text-sm text-gray-700">Revise la lista antes de proceder. Si falta el teléfono y activó el bloqueo, le sugerimos editar dándole al icono de lápiz.</p>

        <div class="signers-review-scroll pr-2 mb-4" style="max-height: 400px; overflow-y: auto;">
          <div v-for="({ signer, originalIndex: index }) in uniqueSignaturesForDisplay" :key="signer.id" class="mb-3 p-3 border border-gray-200 rounded-lg bg-white signer-review-item">
            <div class="flex items-center justify-between">
              <div class="flex items-center flex-1 pr-2">
                <vs-avatar color="primary" :text="(signer.name || 'F').split(' ').slice(0,2).join(' ').trim()" />
                <div class="ml-3 overflow-hidden">
                  <p class="font-bold text-sm mb-0 truncate-name">{{ signer.name || 'Sin nombre' }}</p>
                  <p class="text-xs text-gray-500 mb-0 truncate-info">{{ signer.email }}</p>
                  <p v-if="signer.phone" class="text-xs text-primary mb-0 mt-1 flex items-center font-medium truncate-info">
                    <feather-icon icon="SmartphoneIcon" svgClasses="w-3 h-3 mr-1" />
                    {{ signer.phone }}
                  </p>
                </div>
              </div>
              <div class="flex">
                <vs-button type="border" size="small" icon-pack="feather" :icon="editingSignerIds.includes(signer.id) ? 'icon-check' : 'icon-edit-2'" :color="editingSignerIds.includes(signer.id) ? 'success' : 'primary'" class="mr-1" @click="toggleEditSigner(signer.id)">
                </vs-button>
                <vs-button type="flat" color="danger" size="small" icon-pack="feather" icon="icon-trash-2" @click="removeSignerFromModal(index)"></vs-button>
              </div>
            </div>

            <div class="mt-3 pt-3 border-t border-gray-100" v-if="editingSignerIds.includes(signer.id)">
              <div class="vx-row">
                <div class="vx-col w-full sm:w-1/2 mb-2">
                  <vs-input label="Nombre completo" v-model="signer.name" class="w-full" />
                </div>
                <div class="vx-col w-full sm:w-1/2 mb-2">
                  <vs-input label="Correo electrónico" v-model="signer.email" class="w-full" />
                </div>
                <div class="vx-col w-full mb-2 mt-2">
                  <label class="text-xs text-gray-600 mb-1 block">Teléfono (WhatsApp)</label>
                  <vue-tel-input
                    v-model="signer.phone"
                    @input="(formatted, obj) => onPhoneInputModal(index, formatted, obj)"
                    :inputOptions="telInputOptions.inputOptions"
                    :defaultCountry="telInputOptions.defaultCountry"
                    :enabledFlags="telInputOptions.enabledFlags"
                    :enabledCountryCode="telInputOptions.enabledCountryCode"
                    :preferredCountries="telInputOptions.preferredCountries"
                    :onlyCountries="telInputOptions.onlyCountries"
                    :invalidMsg="telInputOptions.invalidMsg"
                    :mode="telInputOptions.mode"
                    class="vue-tel-input-modal shadow-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div class="text-center mt-4">
            <vs-button type="border" color="primary" icon-pack="feather" icon="icon-user-plus" @click="addNewSignerFromModal">
              Añadir otro firmante a la lista
            </vs-button>
          </div>
        </div>

        <div class="mt-6 flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-100">
          <vs-button type="border" class="w-full sm:w-auto" @click="cancelLightSignatureType">Regresar</vs-button>
          <vs-button type="filled" color="primary" class="w-full sm:w-auto" @click="confirmFinalSignersReview">¡Todo Listo!</vs-button>
        </div>
      </div>
    </vs-popup>

  </div>
</template>

<script>
import planPro from "@/views/pages/buy/plans/PlanPro.vue";
import router from "@/router";
import { generateReferencePdf, isPdfA, PDFA_NOT_SUPPORTED_MESSAGE } from "@/_helpers";
import { documentsService } from "@/_services";
import {FormWizard, TabContent, WizardButton} from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import draggable from 'vuedraggable';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {required, email, helpers, numeric, maxLength, minLength} from 'vuelidate/lib/validators';
import {mapState} from "vuex";

import {ReactInVue} from 'vuera';
import ReactPdfViewer from "../../../../components/viewer-pdf/ReactPdfViewer";
import SignSelector from "../components/SignSelector.vue";
import SignPositionSelector from "../components/SignPositionSelector.vue";
import {SIGN_POSITION} from "../../../../config/app";
import SignerCard from "./SignerCard.vue";
import PdfSignaturePlacer from "./PdfSignaturePlacer.vue";
import { VueTelInput } from 'vue-tel-input';

var rfcRegexValidation = helpers.regex('rfcRegexValidation', /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2})([A\d])$/);
var curpRegexValidation = helpers.regex('curpRegexValidation', /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/);
var phoneRegexValidation = helpers.regex('phoneRegexValidation', /^[0-9]{10}$/);

export default {
  name: "createDocument",
  props: {
    propSignMode: { default: null },
    propSignPosition: { default: null },
    propCompanyId: { default: null }
  },
  components: {
    SignPositionSelector, SignSelector,
    planPro,
    FormWizard,
    TabContent,
    WizardButton,
    draggable,
    flatPickr,
    'ReactPdfViewer': ReactInVue(ReactPdfViewer),
    SignerCard,
    PdfSignaturePlacer,
    VueTelInput
  },
  data() {
    return {
      switch1:true,
      tabIndex: 1,
      focusValidation: false,
      upgradePro: false,
      rememberPopupEnable: false,
      customLogo: false,
      isShowingPdf: true,
      pdf: null,
      base64Pdf: null,
      rawBase64Pdf: null,
      signMode: this.propSignMode || 'PDF_SIGNATURE',
      signPosition: 'CUSTOM',
      company_id: this.propCompanyId,
      restrictSelector: "RFC",
      configDateTimePicker: {
        enableTime: true,
        enableSeconds: true,
        noCalendar: true
      },
      signaturesForm: {
        name: "",
        email: "",
        rfc: "",
      },
      spectatorsForm: {
        name_spectator: "",
        email_spectator: "",
      },
      signatures: [
        {
          id: (Math.random() + 1).toString(36).substring(7),
          name: '',
          email: '',
          restrictSign: false,
          restrictSelector: 'RFC',
          rfc: '',
          restrictMethods: false,
          allowed_signature_methods: null,
        }
      ],
      spectators: [],
      sortableSignatures: false,
      restrictSign: false,
      remember_at: "12:30",
      tries: 3,
      remember_every: 3,
      logoMode: "FULL_BRANDING",
      customBranding: false,
      signatureType: [
        'VERIFICAMEX', 'EFIRMA', 'FIREL', 'VERIFICAMEX_LOCAL'
      ],
      finished: false,
      signedMethodPopupEnable: false,
      signPositionPopupEnable: false,
      spectatorPopupEnable: false,
      emailSuggestions: [],
      lightConfirmationPopup: false,
      blockPhoneVerification: false,
      previousSignatureTypes: [],
      phonesUpdateTrigger: 0,
      signersReviewPopup: false,
      editingSignerIds: [],
      signPositionModalVisible: false,
      telInputOptions: {
        mode: 'international',
        defaultCountry: 'MX',
        disabledFetchingCountry: false,
        enabledCountryCode: true,
        enabledFlags: true,
        preferredCountries: ['MX', 'US', 'ES', 'CO', 'AR', 'CL', 'PE'],
        onlyCountries: [],
        inputOptions: {
          placeholder: 'Número de teléfono',
          showDialCode: true,
        },
        invalidMsg: 'Número de teléfono inválido'
      }
    }
  },
  validations(){
    if (this.restrictSelector === 'RFC') {
      return {
        signaturesForm: {
          name: {
            required
          },
          phone: {
            numeric,
            maxLength: maxLength(10),
            minLength: minLength(10)
          },
          email: {
            required,
            email
          },
          rfc: {
            rfcRegexValidation
          },
        },
        spectatorsForm: {
          name_spectator: {
            required
          },
          email_spectator: {
            required,
            email
          }
        }
      }
    } else {
      return {
        signaturesForm: {
          name: {
            required
          },
          phone: {
            phoneRegexValidation
          },
          email: {
            required,
            email
          },
          rfc: {
            curpRegexValidation
          },
        },
        spectatorsForm: {
          name_spectator: {
          },
          email_spectator: {
            email
          }
        }
      }
    }
  },
  computed: {
    // El detalle de la carpeta activa trae su logo en base64 ("" si no tiene).
    companyHasLogo() {
      const company = this.$store.state.companies.currentCompany;
      return Boolean(company && company.logo);
    },
    customBrandingDescription() {
      if (!this.companyHasLogo) {
        return "Agrega un logo a tu carpeta de trabajo para usarlo en correos y en la pantalla de firma.";
      }
      if (this.logoMode === 'MEDIUM_BRANDING') {
        return "Ya incluido: elegiste tu logo personalizado para la hoja de firmas.";
      }
      return "Usa el logo de tu carpeta en correos y en la pantalla de firma.";
    },
    uniqueSignaturesForDisplay() {
      const seen = new Set();
      return this.signatures.reduce((acc, signer, originalIndex) => {
        if (!signer.email || !seen.has(signer.email)) {
          seen.add(signer.email);
          acc.push({ signer, originalIndex });
        }
        return acc;
      }, []);
    },
    canDisableEfirmaType() {
      let signaturesWithNeededCertificate = this.signatures.filter((signature) => {
        return signature.restrictSelector === 'RFC' && signature.rfc.length;
      })
      return signaturesWithNeededCertificate.length === 0;
    },
    canDisableSimpleAndFirelType() {
      let signaturesWithNeededCertificate = this.signatures.filter((signature) => {
        return signature.restrictSelector === 'CURP' && signature.rfc.length;
      })
      return signaturesWithNeededCertificate.length === 0;
    },
    SIGN_POSITION() {
      return SIGN_POSITION
    },
    isAddMeAsSigner() {
      return this.tabIndex === 1;
    },

    isAddSigners() {
      return this.tabIndex === 2;
    },

    isAddSpectator() {
      return this.tabIndex === 3;
    },

    isSignersResume() {
      return this.isAddSpectator;
    },


    ...mapState({
      user: function (state) {
        return state.account.user;
      },
    }),
    currentUserInSignatures() {
      let isIn = this.signatures.filter(signature => signature.email === this.user.me.email).length;
      if (this.sortableSignatures) {
        let index = 0;
        let position = 0;
        this.signatures.forEach((signature) => {
          if (signature.email === this.user.me.email) {
            position = index;
          }
          index = index + 1;
        });
        return (position === 0) && isIn
      } else {
        return isIn;
      }
    },
    showPdf() {
      return this.base64Pdf;
    },
    hasPhoneNumbers() {
      // Esta línea crea una dependencia para que la computed se recalcule
      this.phonesUpdateTrigger;

      if (!this.signatures || this.signatures.length === 0) return true;
      return this.signatures.every(signer => signer.phone && signer.phone.trim() !== '');
    }
  },
  created() {
    if (!this.company_id && this.user && this.user.me && this.user.me.selected_company) {
      this.company_id = this.user.me.selected_company.id;
    }
    if (!this.signMode) {
      this.signMode = 'PDF_SIGNATURE';
    }

    if (!this.signMode && !this.signPosition && !this.company_id) {
      router.push("/");
    }
    this.$store.dispatch("account/getDefaultSettings").then(
      result => {
        let signers = result.data.find(
          item => item.attributes.settingKey === "default_signatures"
        );
        let viewers = result.data.find(
          item => item.attributes.settingKey === "default_spectators"
        )
        let sortableSignatures = result.data.find(
          item => item.attributes.settingKey === "sortable_signatures"
        )
        let allowSignatureTypes = result.data.find(
          item => item.attributes.settingKey === 'allow_type_signatures'
        )
        let documentMode = result.data.find(
          item => item.attributes.settingKey === 'document_mode'
        )
        let documentSignPosition = result.data.find(
          item => item.attributes.settingKey === "document_sign_position"
        )
        if (signers !== undefined) {
          let signersParsed = JSON.parse(signers.attributes.settingValue)
          if (signersParsed.length) {
            signersParsed = signersParsed.map(signer => {
              signer.id = (Math.random() + 1).toString(36).substring(7);
              if (signer.rfc) {
                signer.restrictSign = true;
                if (signer.rfc.length >= 14) {
                  signer.restrictSelector = 'CURP';
                } else {
                  signer.restrictSelector = 'RFC';
                }
                signer.rfc = signer.rfc.toUpperCase();
              } else {
                signer.restrictSign = false;
                signer.restrictSelector = 'RFC';
                signer.rfc = '';
              }
              return signer;
            })
            this.signatures = signersParsed;
          }
        }
        if (viewers !== undefined) {
          let spectators = JSON.parse(viewers.attributes.settingValue);
          if (spectators.length) {
            this.spectators = spectators;
          }
        }
        if (sortableSignatures !== undefined) {
          this.sortableSignatures = JSON.parse(sortableSignatures.attributes.settingValue)
        }
        if (allowSignatureTypes !== undefined) {
          let signatureTypes = JSON.parse(allowSignatureTypes.attributes.settingValue);
          this.signatureType = !signatureTypes.length ? ['VERIFICAMEX', 'EFIRMA', 'FIREL'] : signatureTypes;
        }
        if (documentMode !== undefined && documentMode !== null) {
          let mode = JSON.parse(documentMode.attributes.settingValue);
          this.signMode = mode == null ? 'PDF_SIGNATURE' : mode;
        }
        if (documentSignPosition !== undefined && documentSignPosition !== null) {
          let position = JSON.parse(documentSignPosition.attributes.settingValue);
          this.signPosition = position == null ? 'NONE' : position;
        }
        // En el flujo beta forzamos siempre PDF_SIGNATURE y CUSTOM
        // para evitar conflictos con la configuración por defecto del usuario
        this.signMode = 'PDF_SIGNATURE';
        this.signPosition = 'CUSTOM';
      }
    )
  },
  methods: {
    resetDocument() {
      this.pdf = null;
      this.base64Pdf = null;
      this.rawBase64Pdf = null;
      this.$refs.fileInput.value = '';
    },
    // Nuevo método para alternar tipos de firma
    toggleSignatureType(type, fromCheckbox = false) {
      // Si el tipo está deshabilitado, no hacer nada
      if (type === 'VERIFICAMEX' && !this.canDisableSimpleAndFirelType) return;
      if (type === 'VERIFICAMEX_LOCAL' && !this.canDisableSimpleAndFirelType) return;
      if (type === 'EFIRMA' && !this.canDisableEfirmaType) return;
      if (type === 'FIREL' && !this.canDisableSimpleAndFirelType) return;

      // Guardar estado actual para el popup de confirmación
      this.previousSignatureTypes = [...this.signatureType];

      // Si no viene del checkbox, nosotros manejamos el toggle manual del array
      // Si viene del checkbox, el v-model ya hizo su trabajo
      if (!fromCheckbox) {
        const index = this.signatureType.indexOf(type);
        if (index > -1) {
          // Si ya está seleccionado, lo removemos
          this.signatureType.splice(index, 1);
        } else {
          // Si no está seleccionado, lo agregamos
          this.signatureType.push(type);
        }
      }

      // Lógica común (popups, etc)
      // Si se selecciona firma simple (ya sea manual o por checkbox), mostrar popup de confirmación
      if (type === 'VERIFICAMEX_LOCAL' && this.signatureType.includes('VERIFICAMEX_LOCAL')) {
        this.lightConfirmationPopup = true;
      }
    },



    addNewSigner() {
      if (this.uniqueSignaturesForDisplay.length >= 10) {
        this.$vs.notify({
          title: 'Límite de firmantes',
          text: 'No puedes agregar más de 10 firmantes únicos al documento.',
          color: 'warning',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center'
        });
        return;
      }
      this.signatures.push({
        id: (Math.random() + 1).toString(36).substring(7),
        name: '',
        email: '',
        phone: '',
        restrictSign: false,
        restrictSelector: 'RFC',
        rfc: '',
      });
    },
    removeSigner(index) {
      this.signatures.splice(index, 1);
    },

    // Modificar validateStep2
    validateStep2() {
      this.$store.dispatch('alert/clear', {});
      return new Promise((resolve, reject) => {
        if (this.signatures.length > 0) {
          if (this.signatures.length === 1 && this.signatures[0].email === '' && this.signatures[0].name === '') {
            this.$store.dispatch('alert/error', 'Por favor, completa la información de todos los firmantes.');
            reject('Por favor, completa la información de todos los firmantes.');
          }
          resolve(true);
        } else {
          this.$store.dispatch('alert/error', 'Por favor, completa la información de todos los firmantes.');
          reject('Por favor, completa la información de todos los firmantes.');
        }
      });
    },

    popUpLimitFunction() {
      this.upgradePro = true;
    },
    openSignPositionPopup() {
      this.signPositionPopupEnable = true;
    },
    setSignPosition(position) {
      this.signPosition = position;
    },
    signSelectorAction(selector) {
      this.signPositionPopupEnable = false;
      this.signedMethodPopupEnable = false;
      
      // Si se selecciona Firma Electrónica Avanzada (Clásica),
      // en el flujo beta la mapeamos a PDF_SIGNATURE con posición NONE (hoja al final bajo estándar PAdES)
      if (selector === 'CLASSIC') {
        this.signMode = 'PDF_SIGNATURE';
        this.signPosition = 'NONE';
        const uniqueSigners = [];
        const seenEmails = new Set();
        for (const s of this.signatures) {
          if (!seenEmails.has(s.email)) {
            s.pos_x = null;
            s.pos_y = null;
            s.page = null;
            uniqueSigners.push(s);
            seenEmails.add(s.email);
          }
        }
        this.signatures = uniqueSigners;
      } else {
        this.signMode = 'PDF_SIGNATURE';
        this.signPosition = 'CUSTOM';
      }
    },
    changeRestrictSelector() {
      if (this.restrictSelector === 'RFC') {
        rfcRegexValidation = helpers.regex('rfcRegexValidation', /^([A-ZÑ&]{3,4})?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01]))?([A-Z\d]{2})([A\d])$/);
      } else {
        curpRegexValidation = helpers.regex('curpRegexValidation', /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/);
      }
    },
    toggleTab(index) {
      this.tabIndex = index;
    },

    btnNext() {
      this.tabIndex = this.tabIndex + 1 ;
    },
    btnBack() {
      this.tabIndex = this.tabIndex - 1 ;
    },
    addMeAsSignature() {
      this.tabIndex = 2 ;
      this.$store.dispatch("alert/clear", {});
      let findSignature = this.signatures.filter((signature) => signature.email === this.user.me.email)
      if (findSignature.length) {
        this.$store.dispatch("alert/error", "Ya estas en la lista");
        return;
      }
      if (this.uniqueSignaturesForDisplay.length >= 10) {
        this.$vs.notify({
          title: 'Límite de firmantes',
          text: 'No puedes agregar más de 10 firmantes únicos al documento.',
          color: 'warning',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center'
        });
        return;
      }
      if (this.signatures.length > 0 && this.signatures[0].email.length === 0) {
        this.signatures = [];
      }
      this.signatures.push({
        id: (Math.random() + 1).toString(36).substring(7),
        name: this.user.me.name + " " + this.user.me.last_name,
        email: this.user.me.email,
        phone: this.user.me.phone,
        restrictSign: false,
        restrictSelector: 'RFC',
        rfc: '',
      });
      this.$store.dispatch("alert/success", "Te has añadido como Firmante");
    },
    deleteMeAsSignature() {
      this.$store.dispatch("alert/clear", {});
      this.signatures = this.signatures.filter((signature) => signature.email !== this.user.me.email)
      this.$store.dispatch("alert/success", "Configurado correctamente");
      this.btnNext();
    },
    deleteSignature(indexSignature) {
      this.$store.dispatch("alert/clear", {});
      this.signatures = this.signatures.filter((signature, index) => index !== indexSignature)
      this.$store.dispatch("alert/success", "Firmante eliminado correctamente");
    },
    deleteSpectator(email) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: '¿Eliminar espectador?',
        text: 'Esta acción no se puede deshacer.',
        acceptText: 'Eliminar',
        cancelText: 'Cancelar',
        accept: () => {
          this.spectators = this.spectators.filter(
            (spectator) => spectator.email !== email
          );
          this.$store.dispatch("alert/success", "Espectador eliminado correctamente.");
        },
      });
    },
    validationEval() {
      if (this.signaturesForm.name || this.signaturesForm.email) {
        this.focusValidation = true;
      } else {
        this.focusValidation = false;
      }
    },
    setUpgradePro(state) {
      this.upgradePro = state;
    },
    setName(value) {
      this.signaturesForm.name = value;
      this.$v.signaturesForm.name.$touch();
    },
    setEmail(value) {
      this.signaturesForm.email = value.replace(/\s+/g, '').trim();
      this.$v.signaturesForm.email.$touch();
    },
    setRfc(value) {
      console.log(this.$v);
      this.signaturesForm.rfc = value;
      this.$v.signaturesForm.rfc.$touch();
    },
    setNameSpectator(value) {
      this.spectatorsForm.name_spectator = value;
      this.$v.spectatorsForm.name_spectator.$touch();
    },
    setEmailSpectator(value) {
      this.spectatorsForm.email_spectator = value.replace(/\s+/g, '').trim();
      this.$v.spectatorsForm.email_spectator.$touch();
    },

    restrictSigner() {
      if (this.restrictSign){
        this.restrictSign = false;
      } else {
        this.restrictSign = true;
      }

    },
    redirectPlans(){
      this.upgradePro = false;
      this.rememberPopupEnable = false;
      this.customLogo = false;
      this.signatureTypes = false;
      setTimeout(() => {
        let routeData = this.$router.resolve({name: 'buy-plans'});
        window.open(routeData.href, '_blank');
        //router.push("/buy/plans");
      }, 100)
    },
    addSpectator() {
      this.$store.dispatch("alert/clear", {});
      this.$v.spectatorsForm.$touch();
      if (this.$v.spectatorsForm.$invalid) {
        this.$store.dispatch("alert/error", "Revise los datos ingresados.");
        return;
      }
      if (this.spectators.length >= 10) {
        this.$vs.notify({
          title: 'Límite de espectadores',
          text: 'No puedes agregar más de 10 espectadores al documento.',
          color: 'warning',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center'
        });
        return;
      }
      const newSpectator = {
        name: this.spectatorsForm.name_spectator.trim(),
        email: this.spectatorsForm.email_spectator.trim(),
      };
      const exists = this.spectators.some(
        (spectator) => spectator.email === newSpectator.email
      );
      if (exists) {
        this.$store.dispatch("alert/error", "El espectador ya está en la lista.");
        return;
      }
      this.spectators.push(newSpectator);
      this.spectatorsForm.name_spectator = "";
      this.spectatorsForm.email_spectator = "";
      this.$v.spectatorsForm.$reset();
      this.$store.dispatch("alert/success", "Espectador añadido correctamente.");
      this.spectatorPopupEnable = false;
    },
    searchSigners(query) {
      this.$store.dispatch("document/searchSigners", {query}).then(
        response => {
          console.log(response)
          this.emailSuggestions = response.data
        }
      ).catch(error => {
        console.log(error)
      })
    },
    addSignature() {
      this.$store.dispatch("alert/clear", {});
      this.$v.signaturesForm.$touch()
      if (this.$v.signaturesForm.$invalid) {
        this.$store.dispatch("alert/error", "Revise los datos ingresados.");
        return;
      }
      if (!this.signaturesForm.name || !this.signaturesForm.email) {
        this.$store.dispatch("alert/error", "Ingrese el nombre y correo electrónico del firmante para continuar");
        return;
      }

      let findSignature = this.signatures.filter((signature) => signature.email === this.signaturesForm.email)
      if (findSignature.length) {
        this.$store.dispatch("alert/error", "El firmante ya se encuentra en la lista");
        return;
      }
      if (this.uniqueSignaturesForDisplay.length >= 10) {
        this.$vs.notify({
          title: 'Límite de firmantes',
          text: 'No puedes agregar más de 10 firmantes únicos al documento.',
          color: 'warning',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center'
        });
        return;
      }
      this.signatures.push({
        id: (Math.random() + 1).toString(36).substring(7),
        name: this.signaturesForm.name.trim(),
        phone: this.signaturesForm.phone.trim(),
        email: this.signaturesForm.email.trim(),
        rfc: this.signaturesForm.rfc.trim()
      });
      this.signaturesForm.name = "";
      this.signaturesForm.phone = "";
      this.signaturesForm.email = "";
      this.signaturesForm.rfc = "";
      this.focusValidation = false;
      this.$store.dispatch("alert/success", "Firmante añadido correctamente");
    },
    formSubmitted(){
      // Asegurar la existencia de company_id y signMode antes de cualquier procesamiento
      if (!this.company_id && this.user && this.user.me && this.user.me.selected_company) {
        this.company_id = this.user.me.selected_company.id;
      }
      if (!this.signMode) {
        this.signMode = 'PDF_SIGNATURE';
      }

      // Validar que todos los firmantes tengan posición si el modo es CUSTOM
      if (this.signPosition === 'CUSTOM') {
        const unplacedSigner = this.signatures.find(s => s.pos_x === null);
        if (unplacedSigner) {
          this.$vs.notify({
            title: 'Faltan posiciones',
            text: `Por favor, coloca la firma para ${unplacedSigner.name || 'todos los firmantes'} en el documento.`,
            color: 'danger',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            position: 'top-center'
          });
          return;
        }
      }

      this.$vs.loading({type: "point"})
      let foundLocal = this.signatureType.find((type) => type === 'VERIFICAMEX_LOCAL');
      let foundVerificamex = this.signatureType.find((type) => type === 'VERIFICAMEX');
      let identityProviders = [];
      if (foundLocal) {
        identityProviders.push("LOCAL");
      }
      if (foundVerificamex) {
        identityProviders.push("VERIFICAMEX");
      }

      // Creamos una copia para no afectar la reactividad en la UI durante el loading
      let typesToSend = [...this.signatureType];

      if (foundLocal && foundVerificamex) {
        typesToSend = typesToSend.filter((type) => type !== 'VERIFICAMEX_LOCAL');
      } else if (foundLocal) {
        typesToSend = typesToSend.filter((type) => type !== 'VERIFICAMEX_LOCAL');
        typesToSend.push('VERIFICAMEX');
      }

      // Construir el array de firmantes con posiciones agrupadas
      // En CUSTOM, agrupamos las instancias duplicadas del mismo firmante en un solo entry con positions[]
      const signerMap = new Map();
      this.signatures.forEach(signer => {
        const key = signer.email;
        if (!signerMap.has(key)) {
          signerMap.set(key, {
            id: signer.id,
            name: signer.name,
            email: signer.email,
            rfc: signer.rfc,
            phone: signer.phone,
            restrictSign: signer.restrictSign,
            restrictSelector: signer.restrictSelector,
            restrictMethods: signer.restrictMethods,
            allowed_signature_methods: signer.allowed_signature_methods,
            positions: [],
          });
        }
        if (this.signPosition === 'CUSTOM' && signer.pos_x !== null && signer.pos_x !== undefined) {
          const w   = signer.width  || 140;
          const h   = signer.height || 80;
          const rot = signer.rotation || 0;
          // Centro del recuadro (invariante ante rotación)
          const cx  = signer.pos_x + w / 2;
          const cy  = signer.pos_y + h / 2;
          // Para 90°/270° el bbox tiene dimensiones intercambiadas
          const bboxW = (rot === 90 || rot === 270) ? h : w;
          const bboxH = (rot === 90 || rot === 270) ? w : h;
          // Esquina superior-izquierda del bbox axis-aligned
          const bboxX = Math.round(cx - bboxW / 2);
          const bboxY = Math.round(cy - bboxH / 2);
          signerMap.get(key).positions.push({
            pos_x: bboxX,
            pos_y: bboxY,
            width: bboxW,
            height: bboxH,
            page: signer.page || 1,
            rotation: rot,
          });
        }
      });
      const signaturesPayload = Array.from(signerMap.values());

      const fullPayload = {
        document: {
          signatures: signaturesPayload,
          spectators: this.spectators,
          pdf: this.pdf,
          sortableSignatures: this.sortableSignatures,
          tries: this.tries,
          remember_every: this.remember_every,
          remember_at: this.remember_at,
          sign_mode: this.signMode,
          sign_position: this.signPosition,
          mode_logo: this.logoMode,
          company_id: this.company_id,
          sign_ordered: this.sortableSignatures ? 'ACTIVE' : 'INACTIVE',
          signatureTypes: typesToSend,
          identityProviders: identityProviders,
          lockPhoneForOtp: this.blockPhoneVerification,
          customBranding: this.companyHasLogo && this.customBranding
        }
      };

      console.log("------- PAYLOAD COMPLETO DE CREACIÓN -------");
      console.log(JSON.stringify(fullPayload, null, 2));
      console.log("------------------------------------------");

      this.$store.dispatch("document/createDocument", fullPayload).then(
        async (document) => {
          try {
            // Generar el PDF de referencia con las firmas estampadas
            const base64ReferencePdf = await generateReferencePdf(this.rawBase64Pdf, this.signatures);
            // Subir el PDF de referencia
            await documentsService.uploadReferenceDocument(document.id, base64ReferencePdf);
          } catch (err) {
            console.error("Error al generar o subir el PDF de referencia:", err);
            this.$store.dispatch("alert/error", "No se pudo guardar el documento de referencia", { root: true });
          }

          this.$vs.loading.close()
          let currentUserSignature = {
            toSign: false,
            url: ""
          }
          if (this.currentUserInSignatures) {
            currentUserSignature.toSign = true;
            let signatureCurrentUser = document.relationships.signatories.data.filter(signature => signature.attributes.email === this.user.me.email)
            currentUserSignature.url = signatureCurrentUser[0].id
          }
          this.finished = true;
          setTimeout(() => {
            router.push({name: "create-document-success", params: currentUserSignature})
          }, 100)
        })
        .catch(
          () => {
            this.$vs.loading.close()
          }
        )
    },
    validateStep1() {
      this.$store.dispatch("alert/clear", {});
      return new Promise((resolve, reject) => {
        if (!this.pdf) {
          reject("Ningún documento ha sido cargado");
          this.$store.dispatch("alert/error", "Ningún documento ha sido cargado");
        } else if (this.pdf.size > 49 * 1024 * 1024) {
          reject("El archivo excede el tamaño máximo permitido de 50 MB");
          this.$store.dispatch("alert/error", "El archivo excede el tamaño máximo permitido de 49 MB");
        } else {
          resolve(true);
        }
      })
    },
    handleDragover (e) {
      e.stopPropagation()
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    },
    handleDrop (e) {
      e.stopPropagation()
      e.preventDefault()
      const files = e.dataTransfer.files
      if (files.length !== 1) {
        this.$vs.notify({
          title: 'Subir',
          text: 'Solo puedes subir un archivo',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning',
          position: 'top-center'
        })
        return
      }
      const rawFile = files[0] // only use files[0]
      this.uploadFile(rawFile)
    },
    handleClick (e) {
      const files = e.target.files
      const rawFile = files[0]
      if (!rawFile) return
      this.uploadFile(rawFile)
    },
    async uploadFile (file) {
      const maxSizeBytes = 49 * 1024 * 1024;
      if (file && file.size > maxSizeBytes) {
        this.$vs.notify({
          title: 'Archivo demasiado grande',
          text: 'El archivo excede el tamaño máximo permitido de 49 MB',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: 'top-center'
        })
        if (this.$refs['fileInput']) {
          this.$refs['fileInput'].value = null
        }
        return
      }
      if (await isPdfA(file)) {
        this.$vs.notify({
          title: 'Formato no compatible',
          text: PDFA_NOT_SUPPORTED_MESSAGE,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: 'top-center',
          time: 8000
        })
        if (this.$refs['fileInput']) {
          this.$refs['fileInput'].value = null
        }
        return
      }
      this.$refs['fileInput'].value = null // fix can't select the same excel
      this.pdf = file;

      // Al cambiar documento: eliminar duplicados y resetear posiciones
      const seenEmails = new Set();
      this.signatures = this.signatures
        .filter(signer => {
          if (!signer.email || !seenEmails.has(signer.email)) {
            seenEmails.add(signer.email);
            return true;
          }
          return false;
        })
        .map(signer => ({
          ...signer,
          pos_x: null,
          pos_y: null,
          page: 1,
          width: 140,
          height: 80,
        }));

      let read = new FileReader();
      read.readAsDataURL(this.pdf);
      let child = this;
      child.isShowingPdf = false;
      read.onloadend = function () {
        console.log("Procesado")
        let dataUrl = read.result;
        child.rawBase64Pdf = dataUrl.split(',')[1];

        // Crear Blob URL para el visor de React (si es necesario)
        let blob = child.b64toBlob(child.rawBase64Pdf, 'application/pdf');
        child.base64Pdf = URL.createObjectURL(blob);
        child.isShowingPdf = true;
      }
    },
    b64toBlob(b64Data, contentType, sliceSize) {
      contentType = contentType || '';
      sliceSize = sliceSize || 512;

      var byteCharacters = atob(b64Data);
      var byteArrays = [];

      for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        var slice = byteCharacters.slice(offset, offset + sliceSize);

        var byteNumbers = new Array(slice.length);
        for (var i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        var byteArray = new Uint8Array(byteNumbers);

        byteArrays.push(byteArray);
      }

      var blob = new Blob(byteArrays, {type: contentType});
      return blob;
    },
    confirmLightSignatureType() {
      if (!this.hasPhoneNumbers && this.blockPhoneVerification) {
        this.$vs.dialog({
          type: 'confirm',
          color: 'warning',
          title: 'Advertencia: Teléfonos no registrados',
          text: 'Algunos firmantes no tienen número de teléfono registrado, lo que podría causar problemas durante el proceso de verificación. ¿Desea continuar de todos modos?',
          acceptText: 'Continuar',
          cancelText: 'Cancelar',
          accept: () => {
            // Guardar la configuración y abrir el segundo popup
            this.savePhoneBlockingConfiguration();
            this.lightConfirmationPopup = false;
            setTimeout(() => {
              this.signersReviewPopup = true;
            }, 300);
          },
          cancel: () => {
            // Volver a la pantalla de configuración sin cerrar
          }
        });
      } else {
        // Guardar la configuración y abrir el segundo popup
        this.savePhoneBlockingConfiguration();
        this.lightConfirmationPopup = false;
        setTimeout(() => {
          this.signersReviewPopup = true;
        }, 300);
      }
    },
    savePhoneBlockingConfiguration() {
      this.$store.dispatch("alert/success", "Configuración de firma simple actualizada correctamente");
    },
    confirmFinalSignersReview() {
      if (this.signatureType.indexOf('VERIFICAMEX_LOCAL') === -1) {
        this.signatureType.push('VERIFICAMEX_LOCAL');
      }
      this.signersReviewPopup = false;
      this.$store.dispatch("alert/success", "Tipo de firma 'Simple' seleccionado correctamente.");
    },
    cancelLightSignatureType() {
      // Al cancelar o ir atrás, nos aseguramos de que no esté seleccionado y cerramos todo
      this.signatureType = this.signatureType.filter(type => type !== 'VERIFICAMEX_LOCAL');
      this.lightConfirmationPopup = false;
      this.signersReviewPopup = false;
      this.editingSignerIds = []; // Limpiar estados de edición
    },
    toggleEditSigner(id) {
      const pos = this.editingSignerIds.indexOf(id);
      if (pos === -1) {
        this.editingSignerIds.push(id);
      } else {
        this.editingSignerIds.splice(pos, 1);
      }
    },
    addNewSignerFromModal() {
      if (this.uniqueSignaturesForDisplay.length >= 10) {
        this.$vs.notify({
          title: 'Límite de firmantes',
          text: 'No puedes agregar más de 10 firmantes únicos al documento.',
          color: 'warning',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center'
        });
        return;
      }
      const newId = (Math.random() + 1).toString(36).substring(7);
      this.signatures.push({
        id: newId,
        name: '',
        email: '',
        phone: '',
        restrictSign: false,
        restrictSelector: 'RFC',
        rfc: '',
      });
      // Abrir automáticamente el modo edición para el nuevo firmante
      this.editingSignerIds.push(newId);
    },
    removeSignerFromModal(index) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: '¿Eliminar firmante?',
        text: 'Esta acción eliminará al firmante de la lista actual.',
        acceptText: 'Eliminar',
        cancelText: 'Cancelar',
        accept: () => {
          this.signatures.splice(index, 1);
          this.$store.dispatch("alert/success", "Firmante eliminado correctamente");
        }
      });
    },
    onPhoneInputModal(index, formattedNumber, { isValid }) {
      if (isValid) {
        this.$set(this.signatures[index], 'phone', formattedNumber);
      } else {
        this.$set(this.signatures[index], 'phone', formattedNumber);
      }
    },
    duplicateSigner(index) {
      const original = this.signatures[index];
      const clone = {
        ...original,
        id: (Math.random() + 1).toString(36).substring(7),
        pos_x: null,
        pos_y: null,
        // Mantener el resto de la info (nombre, email, rfc, phone)
      };
      this.signatures.push(clone);
      this.$store.dispatch("alert/success", `Nueva instancia de firma añadida para ${original.name || original.email}`);
    },
    removeSignerInstance(index) {
      this.signatures.splice(index, 1);
    },
    repeatSignerOnPages({ sourceIndex, pages, pos_x, pos_y, width, height }) {
      const original = this.signatures[sourceIndex];
      pages.forEach(page => {
        this.signatures.push({
          ...original,
          id: (Math.random() + 1).toString(36).substring(7),
          pos_x,
          pos_y,
          width,
          height,
          page,
        });
      });
      this.$store.dispatch('alert/success', `Firma repetida en ${pages.length} hoja${pages.length > 1 ? 's' : ''} más`);
    },
    onPlaceSigner(email) {
      if (this.$refs.pdfPlacer) {
        this.$refs.pdfPlacer.placeNextInstance(email);
      } else {
        this.$store.dispatch('alert/error', 'Primero sube un documento PDF.');
      }
    },
    onClearSigner(email) {
      if (this.$refs.pdfPlacer) {
        this.$refs.pdfPlacer.clearAllInstances(email);
      }
    },
    _hideBeacon() {
      const beaconBtn = document.querySelector('#beacon-container > div > div');
      if (beaconBtn) beaconBtn.style.display = 'none';
    },
    _showBeacon() {
      const beaconBtn = document.querySelector('#beacon-container > div > div');
      if (beaconBtn) beaconBtn.style.display = '';
      if (this._beaconObserver) {
        this._beaconObserver.disconnect();
        this._beaconObserver = null;
      }
    },
  },
  mounted() {
    // Guardar el estado original del menú lateral
    const originalReduceState = this.$store.state.reduceButton;
    localStorage.setItem('originalReduceState', originalReduceState);

    // Activar la minimización si no está ya minimizado
    if (!originalReduceState) {
      this.$store.commit('TOGGLE_REDUCE_BUTTON', true);
    }

    if (document.getElementsByClassName('BeaconFabButtonFrame').item(0)) {
       document.getElementsByClassName('BeaconFabButtonFrame').item(0).classList.add('margin-align')
    }

    // Ocultar el botón de Beacon (puede cargarse de forma asíncrona)
    this._hideBeacon();
    this._beaconObserver = new MutationObserver(() => {
      const beaconBtn = document.querySelector('#beacon-container > div > div');
      if (beaconBtn) {
        beaconBtn.style.display = 'none';
      }
    });
    this._beaconObserver.observe(document.body, { childList: true, subtree: true });
  },
  beforeRouteLeave(to, from, next) {
    // Restaurar el estado original del menú lateral
    const savedState = localStorage.getItem('originalReduceState');
    if (savedState !== null) {
      const shouldReduce = savedState === 'true';
      if (this.$store.state.reduceButton !== shouldReduce) {
        this.$store.commit('TOGGLE_REDUCE_BUTTON', shouldReduce);
      }
      localStorage.removeItem('originalReduceState');
    }

    if (this.finished || (!this.signPosition && !this.signMode && !this.company_id)) {
      next();
      if (document.getElementsByClassName('BeaconFabButtonFrame').item(0)) {
        document.getElementsByClassName('BeaconFabButtonFrame').item(0).classList.remove('margin-align')
      }
      this._showBeacon();
      return;
    }
    this.$vs.dialog({
      type:'confirm',
      color: 'danger',
      title: '¿Estás seguro?',
      text: '¡Todos los cambios se perderan!',
      acceptText: "Si",
      cancelText: "No",
      accept: () => {
        next()
        if (document.getElementsByClassName('BeaconFabButtonFrame').item(0)) {
           document.getElementsByClassName('BeaconFabButtonFrame').item(0).classList.remove('margin-align')
        }
        this._showBeacon();
      },
      cancel: function () {
        next(false)
      }
    });
  },
  watch: {
    // Observar cambios profundos en signatures
    signatures: {
      deep: true,
      handler(newVal) {
        this.phonesUpdateTrigger++;

        // Sincronizar datos del firmante (nombre, teléfono, etc.) desde la primera
        // ocurrencia a todos sus duplicados de posición (mismo email)
        const FIELDS_TO_SYNC = ['name', 'phone', 'rfc', 'restrictSign', 'restrictSelector', 'restrictMethods', 'allowed_signature_methods'];
        const firstOccurrences = {};
        newVal.forEach(signer => {
          if (!signer.email) return;
          if (!firstOccurrences[signer.email]) {
            firstOccurrences[signer.email] = signer;
          } else {
            const source = firstOccurrences[signer.email];
            FIELDS_TO_SYNC.forEach(field => {
              if (signer[field] !== source[field]) {
                this.$set(signer, field, source[field]);
              }
            });
          }
        });
      }
    },
    sortableSignatures(newVal) {
      if (newVal) {
        this.$vs.notify({
          title: 'Firma ordenada habilitada',
          text: 'Se ha habilitado la firma ordenada. Arrastra las tarjetas para establecer el orden de firma y arrastra los recuadros sobre el documento.',
          color: 'primary',
          iconPack: 'feather',
          icon: 'icon-info',
          position: 'top-center'
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* ── Sidebar scrolleable (solo en pantallas grandes) ───────────── */
.sidebar-sticky {
  @media (min-width: 992px) {
    position: sticky;
    top: 80px; /* altura aproximada del navbar superior */
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    overflow-x: hidden;
    /* Scrollbar sutil */
    scrollbar-width: thin;
    scrollbar-color: rgba(46, 0, 149, 0.2) transparent;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(46, 0, 149, 0.2);
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: rgba(46, 0, 149, 0.4);
    }
  }
}

/* ── Sidebar UI components ─────────────────────────────────────── */

/* Ícono del header del sidebar */
.sidebar-header-icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 10px;
  background: rgba(46, 0, 149, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Fila de cabecera de sección (icono + título + info) */
.section-label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.section-label-icon {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-label {
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #484555;
  margin: 0;
  line-height: 1;
}

/* Fila de opción con toggle (firma ordenada) */
.option-row {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8f8f8;
  border: 1px solid rgba(0,0,0,0.06);
  gap: 10px;
  margin-bottom: 16px;
}

.option-row-left {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.option-icon {
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.option-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a1c1c;
  margin: 0 0 2px 0;
  line-height: 1.2;
}

.option-desc {
  font-size: 0.8rem;
  color: #484555;
  margin: 0;
  line-height: 1.4;
  opacity: 0.75;
}



/* Estado vacío compacto */
.empty-state-row {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f3f3f3;
  border: 1px dashed rgba(0,0,0,0.1);
}

/* Sub-cabecera dentro del panel de configuración */
.config-sub-header {
  display: flex;
  align-items: center;
  font-size: 0.84rem;
  font-weight: 700;
  color: #484555;
  margin-bottom: 10px;
}

/* Filas de opciones avanzadas enriquecidas */
.advanced-option-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s ease;
}

.advanced-option-row:hover {
  border-color: #2e0095;
}

.advanced-option-icon {
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 9px;
  background: rgba(46, 0, 149, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;
}

.advanced-option-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a1c1c;
  margin: 0 0 2px 0;
  line-height: 1.2;
}

.advanced-option-desc {
  font-size: 0.78rem;
  color: #484555;
  margin: 0;
  line-height: 1.4;
  opacity: 0.72;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Upload Drop Zone ──────────────────────────────────────────── */
.upload-drop-zone-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.upload-drop-zone {
  width: 100%;
  background: #ffffff;
  border: 2px dashed rgba(46, 0, 149, 0.25);
  border-radius: 20px;
  padding: 64px 36px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 24px rgba(46, 0, 149, 0.06);
}

.upload-drop-zone:hover {
  border-color: rgba(46, 0, 149, 0.55);
  background: rgba(230, 222, 255, 0.12);
}

.upload-icon-box {
  width: 72px;
  height: 72px;
  border-radius: 18px;
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 8px 20px rgba(109, 40, 217, 0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.upload-drop-zone:hover .upload-icon-box {
  transform: translateY(-4px) scale(1.05);
}

.upload-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1a1c1c;
  margin: 0 0 10px 0;
  font-family: 'Manrope', sans-serif;
  letter-spacing: -0.02em;
}

.upload-subtitle {
  font-size: 0.9rem;
  color: #484555;
  margin: 0 0 28px 0;
  line-height: 1.6;
  opacity: 0.8;
}

.upload-hint {
  font-size: 0.76rem;
  color: #484555;
  opacity: 0.5;
  margin: 12px 0 0 0;
}

/* Mapping Design CSS variables based on HTML requirements */
.bg-surface { background-color: #f9f9f9; }
.text-on-surface { color: #1a1c1c; }
.text-on-surface-variant { color: #484555; }
.bg-primary-container { background-color: #4414c7; }
.text-primary-container { color: #4414c7; }
.text-primary { color: #2e0095 !important; }
.bg-primary { background-color: #2e0095; }
.text-white { color: #ffffff; }
.bg-surface-container-high { background-color: #e8e8e8; }
.bg-surface-container-highest { background-color: #e2e2e2; }
.bg-surface-container-lowest { background-color: #ffffff; }
.bg-surface-container-low { background-color: #f3f3f3; }
.bg-secondary-fixed { background-color: #e6deff; }
.text-secondary { color: #60549b; }
.bg-secondary-container { background-color: #bbaffd; }
.bg-primary-fixed { background-color: #e6deff; }
.border-primary-container { border-color: #4414c7; }
.border-outline-variant { border-color: #c9c4d8; }
.font-headline { font-family: 'Manrope', sans-serif; }
.font-body { font-family: 'Manrope', sans-serif; }
.font-label { font-family: 'Inter', sans-serif; }

.editorial-shadow {
  box-shadow: 0 24px 48px -12px rgba(46, 0, 149, 0.06);
}
.digital-curator-theme {
  background-color: #f9f9f9;
}
.rounded-xl {
  border-radius: 1.5rem;
}
.rounded-2xl {
  border-radius: 2rem;
}

/* Nuevos estilos para la sección de tipos de firma */
.signature-types-section {
  background-color: #f7f9fc;
  border-radius: 8px;
  padding: 1.5rem;
}

.signature-types-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-top: 1rem;
}

.signature-types-grid .vs-checkbox--input {
  z-index: 0;
}

.signature-type-card {
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover:not(.disabled) {
    border-color: #7c3aed;
    transform: translateY(-2px);
  }

  &.active {
    border-color: #7c3aed;
    background-color: #f3f0ff;

    .vs-checkbox {
      /deep/ .vs-checkbox--check {
        background: #7c3aed !important;
        border-color: #7c3aed !important;
      }
    }
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;

    .card-content {
      pointer-events: none;
    }
  }

  .card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .signature-icon {
    max-width: 100%;
    max-height: 100%;
  }

  h4 {
    font-size: 0.85rem;
    font-weight: 600;
    margin-bottom: 0.2rem;
    color: #1a202c;
    line-height: 1.1;
  }

  .description {
    font-size: 0.75rem;
    color: #718096;
    margin-bottom: 0.5rem;
  }

  .vs-checkbox {
    margin: 0;

    /deep/ .vs-checkbox--label {
      display: none;
    }
  }
}

/* Estilos existentes */
.position-btn2{
  display: none;
  max-height: 80px!important;
}

.position-btn {
  max-height: 80px!important;
  position: relative;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -500%)!important;
}
@media (max-width: 991px) {
  .position-btn2 {
    display: block;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
}

.position-btn{
  max-height: 50px;
  position: relative;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -500%);
}
.vx-card .vx-card__collapsible-content .vx-card__body {
  padding: .5rem;
}
.grid-view-item {
  .grid-view-img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    transition: .35s;
  }

  &:hover {
    transform: translateY(-5px);

    .grid-view-img{
      opacity: 0.9;
    }
  }
}
.opacity-50{
  opacity: .4!important;
}
.top-x{
  position: relative !important;
  top: -10px!important;
}
.menuicon-float{
  position: relative!important;
  right: 20px;
  top: 23px;
}
.signer-review-item {
  border: 1px solid #f0f0f0;
  background-color: #fcfcfc;
  transition: all 0.2s;
  &:hover {
    border-color: rgba(var(--vs-primary), 1);
    background-color: #fff;
  }
}

.signers-review-scroll {
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
}

/* Estilos para vue-tel-input en el modal */
.vue-tel-input-modal {
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  font-family: inherit;
  width: 100%;
}

.vue-tel-input-modal:focus-within {
  border-color: rgba(var(--vs-primary), 1);
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.05);
}

:deep(.vti__dropdown) {
  padding: 7px;
}

.truncate-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

.truncate-info {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

@media (min-width: 640px) {
  .truncate-name {
    max-width: 250px;
  }
  .truncate-info {
    max-width: 300px;
  }
}

@media (max-width: 640px) {
  .signer-review-item {
    padding: 0.75rem !important;
  }
}

/* PDF Signature Placer */
.placer-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.custom-signers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 12px;
}

.placer-modal-custom {
  :deep(.vs-popup) {
    width: 1000px !important;
    max-width: 95vw;
  }
}
</style>
